/**
 * Input validation utilities for authentication flow
 */

import { createDebugLogger } from './debug';

const logger = createDebugLogger('Validation');

// Email validation regex (RFC 5322 simplified)
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

// Phone validation regex (supports international formats)
const PHONE_REGEX =
  /^[\+]?[(]?[0-9]{1,3}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,4}[-\s\.]?[0-9]{1,9}$/;

// Password strength requirements
const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_REGEX = {
  hasUpperCase: /[A-Z]/,
  hasLowerCase: /[a-z]/,
  hasNumber: /[0-9]/,
  hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/
};

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

export interface PasswordStrength {
  score: number; // 0-4
  feedback: string[];
  isValid: boolean;
}

/**
 * Validate email address format
 */
export function validateEmail(email: string): ValidationResult {
  const errors: string[] = [];

  if (!email || email.trim().length === 0) {
    errors.push('Email is required');
  } else if (!EMAIL_REGEX.test(email.trim())) {
    errors.push('Please enter a valid email address');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Validate phone number format
 */
export function validatePhoneNumber(phone: string): ValidationResult {
  const errors: string[] = [];

  if (!phone || phone.trim().length === 0) {
    errors.push('Phone number is required');
  } else {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length < 10) {
      errors.push('Phone number must be at least 10 digits');
    } else if (cleaned.length > 15) {
      errors.push('Phone number is too long');
    } else if (!PHONE_REGEX.test(phone)) {
      errors.push('Please enter a valid phone number');
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Validate password strength
 */
export function validatePassword(password: string): PasswordStrength {
  const feedback: string[] = [];
  let score = 0;

  if (!password || password.length === 0) {
    return {
      score: 0,
      feedback: ['Password is required'],
      isValid: false
    };
  }

  // Check minimum length
  if (password.length < PASSWORD_MIN_LENGTH) {
    feedback.push(`Password must be at least ${PASSWORD_MIN_LENGTH} characters`);
  } else {
    score++;
  }

  // Check for character types
  if (!PASSWORD_REGEX.hasUpperCase.test(password)) {
    feedback.push('Add at least one uppercase letter');
  } else {
    score++;
  }

  if (!PASSWORD_REGEX.hasLowerCase.test(password)) {
    feedback.push('Add at least one lowercase letter');
  } else {
    score++;
  }

  if (!PASSWORD_REGEX.hasNumber.test(password)) {
    feedback.push('Add at least one number');
  } else {
    score++;
  }

  if (!PASSWORD_REGEX.hasSpecialChar.test(password)) {
    feedback.push('Add at least one special character');
  } else {
    score++;
  }

  // Password is valid if it meets minimum requirements
  const isValid =
    password.length >= PASSWORD_MIN_LENGTH &&
    PASSWORD_REGEX.hasUpperCase.test(password) &&
    PASSWORD_REGEX.hasLowerCase.test(password) &&
    PASSWORD_REGEX.hasNumber.test(password);

  if (isValid && feedback.length === 0) {
    if (score === 5) {
      feedback.push('Strong password!');
    } else {
      feedback.push('Good password');
    }
  }

  return {
    score: Math.min(4, Math.floor(score * 0.8)), // Normalize to 0-4
    feedback,
    isValid
  };
}

/**
 * Validate contact (email or phone)
 */
export function validateContact(
  contact: string
): ValidationResult & { type: 'email' | 'phone' | null } {
  const trimmed = contact.trim();

  // Check if it looks like an email
  if (trimmed.includes('@')) {
    const result = validateEmail(trimmed);
    return { ...result, type: 'email' };
  }

  // Otherwise treat as phone
  const result = validatePhoneNumber(trimmed);
  return { ...result, type: 'phone' };
}

/**
 * Validate OTP code
 */
export function validateOTP(code: string): ValidationResult {
  const errors: string[] = [];
  const cleaned = code.replace(/\D/g, '');

  if (!cleaned || cleaned.length === 0) {
    errors.push('Verification code is required');
  } else if (cleaned.length !== 6) {
    errors.push('Verification code must be 6 digits');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Validate profile data completeness
 */
export function validateProfileData(
  profileType: string,
  data: Record<string, unknown>
): ValidationResult {
  const errors: string[] = [];

  // Define required fields for each profile type
  const requiredFields: Record<string, string[]> = {
    general: ['firstName', 'lastName', 'userTypes'],
    teacher: ['gradeLevel', 'schoolName', 'district', 'city', 'state'],
    student: ['gradeLevel', 'schoolName', 'district', 'city', 'state'],
    parent: ['childGradeLevel', 'schoolName', 'district', 'city', 'state'],
    employer: ['companyName', 'industry', 'title'],
    school: ['schoolName', 'district', 'city', 'state'],
    organizer: ['organizationName', 'role']
  };

  const required = requiredFields[profileType.toLowerCase()];
  if (!required) {
    logger.warn(`Unknown profile type for validation: ${profileType}`);
    return { isValid: true, errors: [] };
  }

  for (const field of required) {
    const value = data[field];
    if (!value || (typeof value === 'string' && value.trim().length === 0)) {
      errors.push(`${field} is required`);
    } else if (Array.isArray(value) && value.length === 0) {
      errors.push(`At least one ${field} must be selected`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Sanitize input to prevent XSS
 */
export function sanitizeInput(input: string): string {
  if (!input) return '';

  return input
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;')
    .trim();
}
